import { action, batch } from '@reatom/framework'

import { Data, pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { pairDtoUpdateReceivedEvent } from '@root/entities/pair'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { ServicesErrorCode } from '@root/shared/api/api-error-code'
import { createCoalescer } from '@root/shared/lib/coalesce-by-key'

import type { TypeHelper } from '@blade/std-lib'
import type {
  ServicesPoolsGetCookingPoolsCreatePayload,
  ServicesPoolsGetNewCreatePayload,
} from '@blade/trade-api-client'
import type { Ctx } from '@reatom/framework'
import type { CurrencyAddress } from '@root/entities/currency'
import type { PairAddress } from '@root/entities/pair'
import type { Error } from '@root/shared'

export const fetchNewPairsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: { network: Network.Network } & TypeHelper.OmitKey<
      ServicesPoolsGetNewCreatePayload,
      'network'
    >,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesPoolsGetNewCreate, [
        {
          ...req,
          network: Network.toDto(network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchNewPairsRequestAction',
)

type SpecialPairsRequestArgs = TypeHelper.OverrideKeys<
  ServicesPoolsGetCookingPoolsCreatePayload,
  {
    network: Network.Network
  }
>

export const fetchCookingPairsRequestAction = action(
  async (ctx, { network, ...req }: SpecialPairsRequestArgs) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.services.servicesPoolsGetCookingPoolsCreate,
        [
          {
            network: Network.toDto(network),
            ...req,
          },
        ],
      ),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchCookingPairsRequestAction',
)

export const fetchFinalStretchPairsRequestAction = action(
  async (ctx, { network, ...req }: SpecialPairsRequestArgs) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.services.servicesPoolsGetFinalStretchCreate,
        [
          {
            network: Network.toDto(network),
            ...req,
          },
        ],
      ),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'fetchFinalStretchPairsRequestAction',
)

export const fetchTopGainersRequestAction = action(
  async (ctx, { network, ...req }: SpecialPairsRequestArgs) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.services.servicesPoolsGetTopGainersCreate,
        [
          {
            network: Network.toDto(network),
            ...req,
          },
        ],
      ),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )
    return mappedResult
  },
  'fetchTopGainersRequestAction',
)

export const fetchGraduatedPairsRequestAction = action(
  async (ctx, { network, ...req }: SpecialPairsRequestArgs) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.services.servicesPoolsGetGraduatedPoolsCreate,
        [
          {
            network: Network.toDto(network),
            ...req,
          },
        ],
      ),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )
    return mappedResult
  },
  'fetchGraduatedPairsRequestAction',
)

export class PairNotFoundError extends Data.TaggedError('PairNotFoundError') {}

/**
 * One `pair-info` request per pair, however many atoms ask for it.
 *
 * The registry answers "already loaded"; nothing answered "loading right
 * now". Two places build a `reatomPairResource` for the same pair — the trade
 * page and the explore-v2 trade screen — and each gets its own resource
 * instance, so on a pair the registry has never seen both miss the cache and
 * both go to the network.
 *
 * Measured on staging opening a pair from the feed: `pair-info` at 739ms and
 * again at 740ms, the second taking 2,966ms and finishing last of everything
 * on the page. The slowest request was a duplicate of the fastest.
 */
const pairRequests = createCoalescer<PairInfoResult>()

/**
 * `abort: 'none'` because this request belongs to no single caller: tied to
 * the first one's controller, the screen that unmounts first would cancel the
 * answer the other is still waiting for. Each caller's own state still
 * follows its own abort — its `ctx.schedule` throws either way — so all this
 * gives up is cancelling a GET nobody wants any more.
 */
const requestPair = async (
  ctx: Ctx,
  address: PairAddress,
  network: Network.Network,
) => {
  const { tradeApiClient } = ctx.get(dependenciesAtom)

  return await apiRequest(
    ctx,
    tradeApiClient.services.servicesPairInfoList,
    [{ pair: address, network: Network.toDto(network) }],
    { abort: 'none' },
  )
}

type PairInfoResult = Awaited<ReturnType<typeof requestPair>>

export const fetchPairRequestAction = action(
  async (
    ctx,
    { address, network }: { address: PairAddress; network: Network.Network },
  ) => {
    const pairResult = await ctx.schedule(() =>
      pairRequests.run(`${network.toString()}|${address.toString()}`, () =>
        requestPair(ctx, address, network),
      ),
    )

    const mappedResult = pipe(
      pairResult,
      Result.mapError((e): PairNotFoundError | Error.UnknownError => {
        if (isPairNotFound(e)) {
          return new PairNotFoundError()
        }

        return ApiError.fallbackToUnknown(e)
      }),
    )

    if (Result.isSuccess(pairResult)) {
      pairDtoUpdateReceivedEvent(ctx, pairResult.success)
    }

    return mappedResult
  },
  'fetchPairRequestAction',
)

const isPairNotFound = ApiError.isOfCodes([
  ServicesErrorCode.PairNotFound,
  ServicesErrorCode.PoolNotFound,
])

export const fetchCurrencyPairsRequestAction = action(
  async (
    ctx,
    {
      currencyAddress,
      network,
    }: {
      currencyAddress: CurrencyAddress
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesGetPoolsList, [
        {
          network: Network.toDto(network),
          tokenAddress: currencyAddress,
        },
      ]),
    )

    if (Result.isSuccess(result)) {
      batch(ctx, () => {
        for (const it of result.success) {
          pairDtoUpdateReceivedEvent(ctx, it)
        }
      })
    }

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )
    return mappedResult
  },
  'fetchCurrencyPairsRequestAction',
)

export const searchPairRequestAction = action(
  async (
    ctx,
    { network, query }: { network: Network.Network; query: string },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesSearchPairList, [
        {
          network: Network.toDto(network),
          query: query,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'searchPairRequestAction',
)
